import React, { useMemo } from 'react';
import { WidgetConfig } from './DraggableWidget';
import { AnimatedKpiCard } from '@/components/analytics/AnimatedKpiCard';
import { SupabaseInventoryChart } from '@/components/inventory/SupabaseInventoryChart';
import { SalesChart } from '@/components/analytics/SalesChart';
import { WeeklySalesChart } from '@/components/inventory/WeeklySalesChart';
import { PaymentMethodChart } from '@/components/charts/PaymentMethodChart';
import { StockLevelChart } from '@/components/inventory/StockLevelChart';
import { WithdrawalsChart } from '@/components/analytics/WithdrawalsChart';
import { WithdrawalsPieChart } from '@/components/analytics/WithdrawalsPieChart';
import { TopWithdrawnProductsTable } from '@/components/analytics/TopWithdrawnProductsTable';
import { SalesListTable } from '@/components/analytics/SalesListTable';
import { InfiniteSalesListTable } from '@/components/analytics/InfiniteSalesListTable';
import { SupabaseProductTable } from '@/components/inventory/SupabaseProductTable';
import { BestSellersTable } from '@/components/analytics/BestSellersTable';
import { RestrictedFeatureGuard } from '@/components/auth/RestrictedFeatureGuard';
import { Package, Euro, AlertTriangle, TrendingUp, ShoppingCart } from 'lucide-react';
import { AddWidgetButton } from './AddWidgetButton';
import { useSales } from '@/hooks/useSales';
import { useWithdrawals } from '@/hooks/useWithdrawals';
import { useSupabaseInventory } from '@/context/SupabaseInventoryContext';
import { CustomDataTableWidget } from '@/components/statistics/CustomDataTableWidget';
import { CustomChartWidget } from '@/components/statistics/CustomChartWidget';
interface DashboardWidgetProps {
  widget: WidgetConfig;
  kpiData?: any;
  managementMode?: string;
  onEditProduct?: (product: any) => void;
  onAddWidget?: (type: string, config: any, index?: number) => void;
  isEditMode?: boolean;
  onEditDataTable?: () => void;
  onChartClick?: (chartComponent: React.ReactNode, title: string) => void;
  onTableClick?: (tableComponent: React.ReactNode, title: string) => void;
  widgetIndex?: number;
  sharedProductSelections?: Set<string>;
  onProductSelectionChange?: (selections: Set<string>) => void;
  onInsightsToggle?: (isOpen: boolean) => void;
}
export const DashboardWidget: React.FC<DashboardWidgetProps> = ({
  widget,
  kpiData,
  managementMode,
  onEditProduct,
  onAddWidget,
  isEditMode,
  onEditDataTable,
  onChartClick,
  onTableClick,
  widgetIndex,
  sharedProductSelections,
  onProductSelectionChange,
  onInsightsToggle
}) => {
  const {
    sales
  } = useSales();
  const {
    withdrawals
  } = useWithdrawals();
  const {
    products
  } = useSupabaseInventory();

  // Create productById lookup object
  const productById = useMemo(() => Object.fromEntries(products.map(p => [p.id, p])), [products]);

  // Add Widget Button (special case)
  if (widget.id === 'add-widget' && isEditMode) {
    return <AddWidgetButton onAdd={onAddWidget!} index={widgetIndex} />;
  }

  // KPI Widgets
  if (widget.type === 'kpi') {
    const getKpiProps = () => {
      switch (widget.config?.kpiType) {
        case 'total-products':
          return {
            title: widget.title,
            value: kpiData?.totalProducts || 0,
            icon: Package,
            colorScheme: 'blue' as const,
            subtitle: 'Total inventaire'
          };
        case 'stock-value':
          return {
            title: widget.title,
            value: kpiData?.totalValue || 0,
            format: 'currency' as const,
            icon: Euro,
            colorScheme: 'green' as const,
            subtitle: 'Valeur totale'
          };
        case 'low-stock':
          const lowStockColorScheme = kpiData?.lowStockCount > 0 ? 'red' : 'green';
          return {
            title: widget.title,
            value: kpiData?.lowStockCount || 0,
            icon: AlertTriangle,
            colorScheme: lowStockColorScheme as 'red' | 'green',
            subtitle: 'Produits sous seuil'
          };
        case 'sales-count':
          return {
            title: widget.title,
            value: managementMode === 'retrait' ? kpiData?.totalWithdrawals : kpiData?.totalSales,
            icon: managementMode === 'retrait' ? Package : TrendingUp,
            colorScheme: 'purple' as const,
            subtitle: managementMode === 'retrait' ? 'Total retraits' : 'Total ventes'
          };
        case 'monthly-revenue':
          return {
            title: widget.title,
            value: kpiData?.monthlyRevenue || 0,
            format: 'currency' as const,
            icon: Euro,
            colorScheme: 'green' as const,
            subtitle: 'Revenus ce mois'
          };
        case 'daily-revenue':
          return {
            title: widget.title,
            value: kpiData?.todayRevenue || 0,
            format: 'currency' as const,
            icon: Euro,
            colorScheme: 'blue' as const,
            subtitle: 'Revenus aujourd\'hui'
          };
        case 'average-order':
          return {
            title: widget.title,
            value: kpiData?.averageOrderValue || 0,
            format: 'currency' as const,
            icon: ShoppingCart,
            colorScheme: 'purple' as const,
            subtitle: 'Panier moyen'
          };
        default:
          return {
            title: widget.title,
            value: 0,
            icon: Package,
            colorScheme: 'blue' as const,
            subtitle: 'Widget KPI'
          };
      }
    };
    return <AnimatedKpiCard {...getKpiProps()} customColor={widget.config?.color} />;
  }

  // Chart Widgets
  if (widget.type === 'chart') {
    const handleChartClick = (chartComponent: React.ReactNode) => {
      if (onChartClick && !isEditMode) {
        onChartClick(chartComponent, widget.title || 'Graphique');
      }
    };
    const renderClickableChart = (chartComponent: React.ReactNode) => <div onClick={() => handleChartClick(chartComponent)} className={!isEditMode ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}>
        {chartComponent}
      </div>;
    switch (widget.config?.chartType) {
      case 'inventory':
        const inventoryChart = <SupabaseInventoryChart onInventoryClick={() => {
          document.getElementById('product-table')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} onLowStockClick={() => {
          document.getElementById('product-table')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} />;
        return renderClickableChart(inventoryChart);
      case 'sales':
        const salesChart = <SalesChart sales={sales} />;
        return <RestrictedFeatureGuard feature="le graphique de ventes détaillé">
            {renderClickableChart(salesChart)}
          </RestrictedFeatureGuard>;
      case 'stock-levels':
        const stockLevelsChart = <StockLevelChart onInventoryClick={() => {
          document.getElementById('product-table')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} onLowStockClick={() => {
          document.getElementById('product-table')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} />;
        return renderClickableChart(stockLevelsChart);
      case 'withdrawals-evolution':
        const withdrawalsChart = <WithdrawalsChart withdrawals={withdrawals} customColor={widget.config?.color} title={widget.title} chartType={widget.config?.displayType as 'bar' | 'line' || 'bar'} />;
        return renderClickableChart(withdrawalsChart);
      case 'withdrawals-pie':
        const withdrawalsPieChart = <WithdrawalsPieChart withdrawals={withdrawals} productById={productById} customColor={widget.config?.color} title={widget.title} />;
        return renderClickableChart(withdrawalsPieChart);
      case 'weekly-sales':
        const weeklySalesChart = <SalesChart sales={sales} customColor={widget.config?.color} />;
        return <RestrictedFeatureGuard feature="le graphique de ventes détaillé">
            {renderClickableChart(weeklySalesChart)}
          </RestrictedFeatureGuard>;
      case 'payment-methods':
        const paymentChart = <PaymentMethodChart />;
        return renderClickableChart(paymentChart);
      case 'daily-sales':
        const dailySalesChart = <SalesChart sales={sales} customColor={widget.config?.color} />;
        return <RestrictedFeatureGuard feature="le graphique de ventes détaillé">
            {renderClickableChart(dailySalesChart)}
          </RestrictedFeatureGuard>;
      case 'revenue-trend':
        const revenueTrendChart = <SalesChart sales={sales} customColor={widget.config?.color} />;
        return <RestrictedFeatureGuard feature="le graphique des tendances de revenus">
            {renderClickableChart(revenueTrendChart)}
          </RestrictedFeatureGuard>;
      case 'stock-movement':
        const stockMovementChart = <SupabaseInventoryChart onInventoryClick={() => {
          document.getElementById('product-table')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} onLowStockClick={() => {
          document.getElementById('product-table')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} />;
        return renderClickableChart(stockMovementChart);
      case 'category-breakdown':
        const categoryChart = <PaymentMethodChart />;
        return renderClickableChart(categoryChart);
      default:
        return <div className="h-64 flex items-center justify-center text-muted-foreground border-2 border-dashed rounded-lg">
            Graphique non configuré
          </div>;
    }
  }

  // Table Widgets
  if (widget.type === 'table') {
    switch (widget.config?.tableType) {
      case 'products':
        return <div id="product-table">
            <SupabaseProductTable 
              onEditProduct={onEditProduct}
              initialSelectedProducts={sharedProductSelections}
              onSelectionChange={onProductSelectionChange}
            />
          </div>;
      case 'sales-list':
        const salesListTable = <SalesListTable 
          sales={sales} 
          productById={productById} 
          statusFilter="all" 
          currentPage={1} 
          rowsPerPage={10} 
          totalPages={1}
        />;
        return <RestrictedFeatureGuard feature="Liste des ventes">
            {salesListTable}
          </RestrictedFeatureGuard>;
      case 'top-withdrawn':
        const topWithdrawnTable = <TopWithdrawnProductsTable withdrawals={withdrawals} productById={productById} />;
        return <RestrictedFeatureGuard feature="Produits les plus retirés">
            {topWithdrawnTable}
          </RestrictedFeatureGuard>;
      case 'best-sellers':
        const bestSellersTable = <BestSellersTable periodFilter="week" />;
        return <RestrictedFeatureGuard feature="Meilleurs vendeurs">
            {bestSellersTable}
          </RestrictedFeatureGuard>;
      default:
        return <div className="h-64 flex items-center justify-center text-muted-foreground border-2 border-dashed rounded-lg">
            <div className="text-center">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Tableau "{widget.config?.tableType || 'inconnu'}" en développement</p>
            </div>
          </div>;
    }
  }

  // Main widget render logic
  if (widget.type === 'custom-table') {
    return <CustomDataTableWidget 
      config={widget.config || {}} 
      onEdit={!isEditMode ? onEditDataTable : undefined}
      onInsightsToggle={onInsightsToggle}
    />;
  }
  if (widget.type === 'custom-chart') {
    const customChart = <CustomChartWidget 
      config={widget.config || {}}
      onInsightsToggle={onInsightsToggle}
    />;
    if (onChartClick && !isEditMode) {
      return <div onClick={() => onChartClick(customChart, widget.title || 'Graphique personnalisé')} className="cursor-pointer hover:opacity-80 transition-opacity">
          {customChart}
        </div>;
    }
    return customChart;
  }

  // Default fallback
  return;
};